import { useState, useEffect, useRef } from "react";
import { UserPlus, Search, MessageCircle, FileCheck, DollarSign, Target, ArrowRight, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/contexts/LanguageContext";

function useReveal<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add("visible");
          observer.unobserve(el);
        }
      },
      { threshold: 0.15 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return ref;
}

const studentStepDefs = [
  { icon: Target, titleKey: "how.student.step1.title", descKey: "how.student.step1.desc", link: "/how-it-works/goals", linkKey: "how.student.step1.link", gradient: "from-[hsl(39,96%,48%)] to-[hsl(30,90%,45%)]", shadow: "shadow-[hsl(39,96%,48%)]/20" },
  { icon: Search, titleKey: "how.student.step2.title", descKey: "how.student.step2.desc", link: "/how-it-works/matching", linkKey: "how.student.step2.link", gradient: "from-[hsl(260,60%,55%)] to-[hsl(280,55%,50%)]", shadow: "shadow-[hsl(260,60%,55%)]/20" },
  { icon: MessageCircle, titleKey: "how.student.step3.title", descKey: "how.student.step3.desc", link: "/how-it-works/first-session", linkKey: "how.student.step3.link", gradient: "from-[hsl(35,95%,55%)] to-[hsl(25,90%,50%)]", shadow: "shadow-[hsl(35,95%,55%)]/20" },
];

const coachStepDefs = [
  { icon: UserPlus, titleKey: "how.coach.step1.title", descKey: "how.coach.step1.desc", link: "/how-it-works/apply", linkKey: "how.coach.step1.link", gradient: "from-[hsl(39,96%,48%)] to-[hsl(30,90%,45%)]", shadow: "shadow-[hsl(39,96%,48%)]/20" },
  { icon: FileCheck, titleKey: "how.coach.step2.title", descKey: "how.coach.step2.desc", link: "/how-it-works/verification", linkKey: "how.coach.step2.link", gradient: "from-[hsl(260,60%,55%)] to-[hsl(280,55%,50%)]", shadow: "shadow-[hsl(260,60%,55%)]/20" },
  { icon: DollarSign, titleKey: "how.coach.step3.title", descKey: "how.coach.step3.desc", link: "/how-it-works/earning", linkKey: "how.coach.step3.link", gradient: "from-[hsl(35,95%,55%)] to-[hsl(25,90%,50%)]", shadow: "shadow-[hsl(35,95%,55%)]/20" },
];

export function HowItWorks() {
  const { t } = useLanguage();
  const [activePersona, setActivePersona] = useState<"student" | "coach">("student");
  const stepDefs = activePersona === "student" ? studentStepDefs : coachStepDefs;
  const headerRef = useReveal<HTMLDivElement>();
  const stepsRef = useReveal<HTMLDivElement>();

  return (
    <section id="how-it-works" className="py-28 bg-white/30 backdrop-blur-sm relative overflow-hidden">
      {/* Decorative background blobs */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-[hsl(39,96%,48%)]/[0.05] rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-72 h-72 bg-[hsl(260,60%,55%)]/[0.04] rounded-full blur-[100px] pointer-events-none" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-[hsl(35,95%,55%)]/[0.03] rounded-full blur-[120px] pointer-events-none" />

      <div className="container mx-auto px-4 relative z-10">
        <div ref={headerRef} className="text-center max-w-2xl mx-auto mb-14 reveal">
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-[hsl(237,52%,50%)] mb-3 px-3 py-1 rounded-full bg-[hsl(237,52%,31%)]/10">
            <Sparkles className="w-3 h-3" />
            {t("how.badge")}
          </span>
          <h2 className="font-sans text-4xl sm:text-5xl text-foreground mb-8">
            {t("how.headline")}{" "}
            <span className="text-gradient">{t("how.clarity")}</span>
          </h2>

          {/* Persona Toggle */}
          <div className="inline-flex items-center rounded-full bg-white/50 backdrop-blur-md p-1.5 gap-1 border border-white/70 shadow-md">
            <button
              onClick={() => setActivePersona("student")}
              className={cn(
                "px-6 py-2.5 rounded-full text-sm font-semibold transition-all duration-300",
                activePersona === "student"
                  ? "bg-gradient-to-r from-[hsl(237,52%,31%)] to-[hsl(237,52%,22%)] text-primary-foreground shadow-lg"
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              {t("how.student")}
            </button>
            <button
              onClick={() => setActivePersona("coach")}
              className={cn(
                "px-6 py-2.5 rounded-full text-sm font-semibold transition-all duration-300",
                activePersona === "coach"
                  ? "bg-gradient-to-r from-[hsl(237,52%,31%)] to-[hsl(237,52%,22%)] text-primary-foreground shadow-lg"
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              {t("how.coach")}
            </button>
          </div>
        </div>

        <div ref={stepsRef} className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto relative reveal">
          {/* Connecting line (desktop) */}
          <div className="hidden md:block absolute top-[5rem] left-[20%] right-[20%] h-px z-0">
            <div className="w-full h-full bg-gradient-to-r from-[hsl(237,52%,31%)]/30 via-[hsl(260,60%,55%)]/30 to-[hsl(35,95%,55%)]/30" />
          </div>

          {stepDefs.map((step, index) => (
            <div
              key={`${activePersona}-${index}`}
              className="relative text-center group z-10"
              style={{ animationDelay: `${index * 0.15}s` }}
            >
              {/* Card wrapper */}
              <div className="rounded-2xl bg-white/50 backdrop-blur-md border border-white/70 p-8 shadow-[0_4px_24px_rgba(38,42,122,0.08)] hover:shadow-[0_8px_40px_rgba(38,42,122,0.16)] hover:bg-white/65 transition-all duration-300 hover:-translate-y-1">
                {/* Step icon with gradient background */}
                <div className="relative inline-flex items-center justify-center mb-6">
                  <div className={cn(
                    "w-16 h-16 rounded-2xl flex items-center justify-center bg-gradient-to-br shadow-lg",
                    step.gradient, step.shadow
                  )}>
                    <step.icon className="w-7 h-7 text-white" />
                  </div>
                  {/* Step badge */}
                  <span className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-foreground text-background text-xs font-bold flex items-center justify-center shadow-md ring-2 ring-card">
                    {index + 1}
                  </span>
                </div>

                <h3 className="font-sans text-2xl text-foreground mb-3 group-hover:text-[hsl(237,52%,31%)] transition-colors">
                  {t(step.titleKey)}
                </h3>
                <p className="text-muted-foreground mb-5 leading-relaxed text-sm max-w-xs mx-auto">
                  {t(step.descKey)}
                </p>
                <Link
                  to={step.link}
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-[hsl(237,52%,50%)] hover:underline underline-offset-4 transition-all group/link"
                >
                  {t(step.linkKey)}
                  <ArrowRight className="w-3.5 h-3.5 group-hover/link:translate-x-0.5 transition-transform" />
                </Link>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
